import classNames from 'classnames';
import { Moment } from 'moment';
import React from 'react';

import { ClassNameMap, makeStyles } from '@mui/styles';

import { translate } from '@vestahealthcare/common/i18n';
import { MemberDashboardSkilled } from '@vestahealthcare/common/models';
import {
  DATE_FORMAT_SHORT,
  EMPTY,
} from '@vestahealthcare/common/utils/constants';

import { Chip, StatusIndicator, Table } from 'styleguide-v2';

import { capitalize } from 'lodash';

type Props = {
  className?: string;
  data?: MemberDashboardSkilled[];
};

const getDateWithDiffDays = (date?: Moment, days?: number) => (
  <>
    {date?.format(DATE_FORMAT_SHORT) || EMPTY}{' '}
    {date && days !== undefined && (
      <Chip color="action" size="small">
        <span>{days}</span>
      </Chip>
    )}
  </>
);

const getSkilledColor = (status: string) => {
  if (status.toLocaleUpperCase() === 'OPEN') return 'success';
  if (status.toLocaleUpperCase() === 'CLOSED') return 'closed';
  return 'warning';
};

const RECENT_SKILLED_COLUMNS = (styles: ClassNameMap<'status'>) => [
  {
    headerName: 'Status',
    component: ({ closedAt, status }: MemberDashboardSkilled) => (
      <StatusIndicator
        className={styles.status}
        color={getSkilledColor(status)}
        status={capitalize(status)}
        subtitle={closedAt?.format(DATE_FORMAT_SHORT)}
      />
    ),
  },
  {
    headerName: translate('toc.table.episodeStartDate'),
    component: ({ startDate, episodeDuration }: MemberDashboardSkilled) => (
      <p className="no-margin">
        {getDateWithDiffDays(startDate, episodeDuration)}
      </p>
    ),
  },
  {
    headerName: translate('toc.table.episodeEndDate'),
    component: ({
      endDate,
      businessDaysSinceEndDate,
    }: MemberDashboardSkilled) => (
      <p className="no-margin">
        {getDateWithDiffDays(endDate, businessDaysSinceEndDate)}
      </p>
    ),
  },
];

const useStyles = makeStyles({
  table: {
    marginTop: 1,
  },
  status: {
    '& > :first-child': {
      margin: '4px 0 0',
    },
    '& > :last-child': {
      marginTop: '-3px',
    },
  },
});

export const RecentSkilledTable = (props: Props) => {
  const styles = useStyles();

  return (
    <Table
      className={classNames(styles.table, props.className)}
      config={{
        columns: RECENT_SKILLED_COLUMNS(styles),
        compact: true,
        data: props.data || [],
        paginationOptions: [-1],
      }}
      empty={translate('memberProfile.dashboard.noRecentSkilled')}
      fontSize="small"
      whiteBackground
    />
  );
};
